import {
  IMAGE_UNLOCK_STATE_FIXTURES,
  RESULT_SCOPE_UNLOCK_STATE_FIXTURES
} from '@/core/credit/mocks/stateFixtures';
import { buildResultAccessScope } from '@/core/credit/resultAccessScope';

export const UNLOCK_MOCK_SCENARIOS = Object.freeze({
  SUCCESS: 'success',
  UNLOCKED: 'unlocked',
  EMPTY_UNLOCK_SET: 'empty_unlock_set',
  TIMEOUT: 'timeout',
  INVALID_PAYLOAD: 'invalid_payload',
  SAME_IMAGE_REVISIT: 'same_image_revisit',
  REFRESH_RESTORE: 'refresh_restore',
  CROSS_USER: 'cross_user',
  CROSS_IMAGE: 'cross_image',
  SAME_SCOPE_REVISIT: 'same_scope_revisit',
  CROSS_SCOPE_ISOLATED: 'cross_scope_isolated',
  SOLO_SCOPE_ISOLATED: 'solo_scope_isolated'
});

const DEFAULT_IMAGE_ID = 'image-1';
const DEFAULT_UPDATED_AT = 1711843200000;
const DEFAULT_VERSION = 'mock-v1';

const GENERIC_UNLOCKED_FEATURES = Object.freeze([
  Object.freeze({ featureCode: 'compare', subFeatureCode: 'point_inspector' })
]);

const clone = (value) => JSON.parse(JSON.stringify(value));

const toTrimmedString = (value) => String(value || '').trim();

const resolveScope = ({ originalHashId, imageId, presetStyleId, scopeType, scopeKey }) => {
  const normalizedScopeKey = toTrimmedString(scopeKey);
  if (normalizedScopeKey) {
    return {
      scopeKey: normalizedScopeKey,
      scopeType: toTrimmedString(scopeType) || 'solo',
      originalHashId: toTrimmedString(originalHashId || imageId),
      presetStyleId: toTrimmedString(presetStyleId) || null
    };
  }

  const scope = buildResultAccessScope({
    originalHashId: toTrimmedString(originalHashId || imageId),
    presetStyleId: toTrimmedString(presetStyleId) || null,
    scopeType: toTrimmedString(scopeType) || undefined
  });

  return scope || {
    scopeKey: '',
    scopeType: 'solo',
    originalHashId: toTrimmedString(originalHashId || imageId),
    presetStyleId: null
  };
};

const toTransportFeatures = (features = []) => {
  if (!Array.isArray(features)) {
    return features;
  }

  return features.map((item) => {
    if (!item || typeof item !== 'object') {
      return item;
    }

    return {
      feature_code: toTrimmedString(item.featureCode || item.feature_code),
      sub_feature_code: toTrimmedString(item.subFeatureCode || item.sub_feature_code || 'default') || 'default'
    };
  });
};

const buildTransportPayloadFromFixture = (fixture, scope) => ({
  scope_key: toTrimmedString(fixture?.scopeKey || scope.scopeKey),
  scope_type: toTrimmedString(fixture?.scopeType || scope.scopeType || 'solo') || 'solo',
  original_hash_id: toTrimmedString(fixture?.originalHashId || scope.originalHashId) || DEFAULT_IMAGE_ID,
  preset_style_id: toTrimmedString(fixture?.presetStyleId || scope.presetStyleId) || null,
  image_id: toTrimmedString(fixture?.imageId || fixture?.originalHashId || scope.originalHashId) || DEFAULT_IMAGE_ID,
  unlocked_features: toTransportFeatures(clone(fixture?.unlockedFeatures || [])),
  updated_at: fixture?.updatedAt || DEFAULT_UPDATED_AT,
  version: fixture?.version || DEFAULT_VERSION
});

export const resolveUnlockMockScenario = (scenarioId) => {
  const normalized = toTrimmedString(scenarioId).toLowerCase();
  if (!normalized) {
    return UNLOCK_MOCK_SCENARIOS.SUCCESS;
  }

  const values = Object.values(UNLOCK_MOCK_SCENARIOS);
  return values.includes(normalized)
    ? normalized
    : UNLOCK_MOCK_SCENARIOS.SUCCESS;
};

const buildDefaultSuccessPayload = ({ userId, scope }) => {
  const normalizedUserId = toTrimmedString(userId).toLowerCase();

  if (normalizedUserId === 'user-b' || normalizedUserId === 'user-2') {
    return buildTransportPayloadFromFixture(RESULT_SCOPE_UNLOCK_STATE_FIXTURES.cross_user_scope, scope);
  }

  if (scope.scopeKey === 'history-image-1::preset-1') {
    return buildTransportPayloadFromFixture(RESULT_SCOPE_UNLOCK_STATE_FIXTURES.same_scope_revisit, scope);
  }

  if (scope.scopeKey === 'history-image-1::preset-2') {
    return buildTransportPayloadFromFixture(RESULT_SCOPE_UNLOCK_STATE_FIXTURES.another_reference_locked, scope);
  }

  if (scope.scopeKey === 'history-image-1::__solo__') {
    return buildTransportPayloadFromFixture(RESULT_SCOPE_UNLOCK_STATE_FIXTURES.solo_scope_locked, scope);
  }

  return {
    scope_key: scope.scopeKey,
    scope_type: scope.scopeType,
    original_hash_id: scope.originalHashId || DEFAULT_IMAGE_ID,
    preset_style_id: scope.presetStyleId,
    image_id: scope.originalHashId || DEFAULT_IMAGE_ID,
    unlocked_features: toTransportFeatures(clone(GENERIC_UNLOCKED_FEATURES)),
    updated_at: DEFAULT_UPDATED_AT,
    version: DEFAULT_VERSION
  };
};

export const buildUnlockMockTransportPayload = ({
  userId,
  imageId,
  originalHashId,
  presetStyleId,
  scopeType,
  scopeKey,
  scenarioId
} = {}) => {
  const resolvedScenarioId = resolveUnlockMockScenario(scenarioId);
  const scope = resolveScope({
    originalHashId,
    imageId,
    presetStyleId,
    scopeType,
    scopeKey
  });

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.EMPTY_UNLOCK_SET) {
    return {
      scope_key: scope.scopeKey,
      scope_type: scope.scopeType,
      original_hash_id: scope.originalHashId || DEFAULT_IMAGE_ID,
      preset_style_id: scope.presetStyleId,
      image_id: scope.originalHashId || DEFAULT_IMAGE_ID,
      unlocked_features: [],
      updated_at: DEFAULT_UPDATED_AT,
      version: 'mock-empty-v1'
    };
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.INVALID_PAYLOAD) {
    return {
      scope_key: scope.scopeKey,
      scope_type: scope.scopeType,
      original_hash_id: scope.originalHashId || DEFAULT_IMAGE_ID,
      preset_style_id: scope.presetStyleId,
      image_id: scope.originalHashId || DEFAULT_IMAGE_ID,
      unlocked_features: 'invalid-unlocked-features',
      updated_at: DEFAULT_UPDATED_AT,
      version: 'mock-invalid-v1'
    };
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.SAME_SCOPE_REVISIT) {
    return buildTransportPayloadFromFixture(RESULT_SCOPE_UNLOCK_STATE_FIXTURES.same_scope_revisit, scope);
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.CROSS_SCOPE_ISOLATED) {
    return buildTransportPayloadFromFixture(RESULT_SCOPE_UNLOCK_STATE_FIXTURES.another_reference_locked, scope);
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.SOLO_SCOPE_ISOLATED) {
    return buildTransportPayloadFromFixture(RESULT_SCOPE_UNLOCK_STATE_FIXTURES.solo_scope_locked, scope);
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.SAME_IMAGE_REVISIT) {
    return buildTransportPayloadFromFixture(IMAGE_UNLOCK_STATE_FIXTURES.same_image_revisit, scope);
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.REFRESH_RESTORE) {
    return buildTransportPayloadFromFixture(IMAGE_UNLOCK_STATE_FIXTURES.refresh_restore, scope);
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.CROSS_USER) {
    return buildTransportPayloadFromFixture(IMAGE_UNLOCK_STATE_FIXTURES.cross_user, scope);
  }

  if (resolvedScenarioId === UNLOCK_MOCK_SCENARIOS.CROSS_IMAGE) {
    return buildTransportPayloadFromFixture(IMAGE_UNLOCK_STATE_FIXTURES.cross_image, scope);
  }

  return buildDefaultSuccessPayload({ userId, scope });
};
